import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Save, Download, Eye, CheckCircle, AlertCircle,
  Building, Target, DollarSign, Users, BookOpen, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const CSRPolicyBuilderPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    cin: '',
    registeredOffice: '',
    incorporationYear: '',
    sector: '',
    
    // Financial Information
    netWorth: '',
    turnover: '',
    netProfit: '',
    avgNetProfit3Years: '',
    csrBudget: '',
    
    // CSR Committee
    committeeMembers: [
      { name: '', designation: '', role: 'Member' }
    ],
    
    // Focus Areas
    focusAreas: [],
    geographicalFocus: '',
    targetBeneficiaries: '',
    
    // Implementation Strategy
    implementationMode: 'direct',
    partnershipPreference: '',
    monitoringMechanism: '',
    
    // Compliance
    policyApprovalDate: '',
    reviewFrequency: 'annual',
    reportingTimeline: ''
  });

  const csrFocusOptions = [
    'Eradicating hunger, poverty and malnutrition',
    'Promoting education',
    'Promoting gender equality and empowering women',
    'Reducing child mortality and improving maternal health',
    'Combating HIV/AIDS, malaria and other diseases',
    'Ensuring environmental sustainability',
    'Employment enhancing vocational skills',
    'Social business projects',
    'Rural development projects',
    'Slum area development',
    'Protection of national heritage, art and culture'
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleFocusArea = (area) => {
    const updated = formData.focusAreas.includes(area)
      ? formData.focusAreas.filter(a => a !== area)
      : [...formData.focusAreas, area];
    setFormData({ ...formData, focusAreas: updated });
  };

  const addCommitteeMember = () => {
    setFormData({
      ...formData,
      committeeMembers: [...formData.committeeMembers, { name: '', designation: '', role: 'Member' }]
    });
  };

  const updateCommitteeMember = (index, field, value) => {
    const updated = [...formData.committeeMembers];
    updated[index][field] = value;
    setFormData({ ...formData, committeeMembers: updated });
  };

  const removeCommitteeMember = (index) => {
    const updated = formData.committeeMembers.filter((_, i) => i !== index);
    setFormData({ ...formData, committeeMembers: updated });
  };

  const handleGeneratePolicy = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${apiUrl}/api/consultancy/policy-builder/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const result = await res.json();
        setPolicy(result);
        setSuccess('CSR Policy generated successfully!');
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to generate policy');
      }
    } catch (error) {
      console.error('Error generating policy:', error);
      setError('An error occurred while generating the policy');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (format = 'pdf') => {
    if (!policy) return;

    if (format === 'pdf') {
      // Create a downloadable PDF
      const pdfContent = policy.policyDocument || policy.document || '';
      const blob = new Blob([pdfContent], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `csr-policy-${formData.companyName.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (format === 'docx') {
      // For Word document
      const docContent = policy.policyDocument || policy.document || '';
      const blob = new Blob([docContent], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `csr-policy-${formData.companyName.replace(/\s+/g, '-').toLowerCase()}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const steps = [
    { number: 1, title: 'Company Info', icon: Building },
    { number: 2, title: 'Financial Data', icon: DollarSign },
    { number: 3, title: 'CSR Committee', icon: Users },
    { number: 4, title: 'Focus Areas', icon: Target },
    { number: 5, title: 'Implementation', icon: BookOpen },
    { number: 6, title: 'Review', icon: Eye }
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CIN *</label>
                <input
                  type="text"
                  value={formData.cin}
                  onChange={(e) => handleInputChange('cin', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Corporate Identification Number"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Registered Office *</label>
                <textarea
                  value={formData.registeredOffice}
                  onChange={(e) => handleInputChange('registeredOffice', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows="2"
                  placeholder="Full registered office address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year of Incorporation *</label>
                <input
                  type="number"
                  value={formData.incorporationYear}
                  onChange={(e) => handleInputChange('incorporationYear', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 2010"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Sector *</label>
                <input
                  type="text"
                  value={formData.sector}
                  onChange={(e) => handleInputChange('sector', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., IT Services, Manufacturing"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
            <p className="text-sm text-gray-600 mb-4">
              As per Section 135 of Companies Act, 2013
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Net Worth (₹) *</label>
                <input
                  type="number"
                  value={formData.netWorth}
                  onChange={(e) => handleInputChange('netWorth', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 50000000000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Turnover (₹) *</label>
                <input
                  type="number"
                  value={formData.turnover}
                  onChange={(e) => handleInputChange('turnover', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 100000000000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Net Profit (₹) *</label>
                <input
                  type="number"
                  value={formData.netProfit}
                  onChange={(e) => handleInputChange('netProfit', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 5000000000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Avg Net Profit (3 Years) (₹) *</label>
                <input
                  type="number"
                  value={formData.avgNetProfit3Years}
                  onChange={(e) => handleInputChange('avgNetProfit3Years', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 4500000000"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Proposed CSR Budget (₹) *</label>
                <input
                  type="number"
                  value={formData.csrBudget}
                  onChange={(e) => handleInputChange('csrBudget', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="Minimum 2% of average net profit"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recommended: ₹{(parseFloat(formData.avgNetProfit3Years || 0) * 0.02).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">CSR Committee Members</h3>
              <Button
                onClick={addCommitteeMember}
                variant="outline"
                size="sm"
                className="text-teal-600"
              >
                + Add Member
              </Button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Minimum 3 members required, including at least one independent director
            </p>
            <div className="space-y-4">
              {formData.committeeMembers.map((member, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={member.name}
                        onChange={(e) => updateCommitteeMember(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                      <input
                        type="text"
                        value={member.designation}
                        onChange={(e) => updateCommitteeMember(index, 'designation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g., Director"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={member.role}
                          onChange={(e) => updateCommitteeMember(index, 'role', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="Member">Member</option>
                          <option value="Chairperson">Chairperson</option>
                        </select>
                      </div>
                      {formData.committeeMembers.length > 1 && (
                        <button
                          onClick={() => removeCommitteeMember(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">CSR Focus Areas</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select your priority areas as per Schedule VII of the Companies Act
            </p>
            <div className="space-y-2">
              {csrFocusOptions.map((area, index) => (
                <label key={index} className="flex items-start p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.focusAreas.includes(area)}
                    onChange={() => toggleFocusArea(area)}
                    className="mt-1 mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{area}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Geographical Focus</label>
                <input
                  type="text"
                  value={formData.geographicalFocus}
                  onChange={(e) => handleInputChange('geographicalFocus', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Pan-India, Maharashtra, Rural areas"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Beneficiaries</label>
                <input
                  type="text"
                  value={formData.targetBeneficiaries}
                  onChange={(e) => handleInputChange('targetBeneficiaries', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Women, Children, Rural communities"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Implementation Strategy</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Implementation Mode *</label>
                <select
                  value={formData.implementationMode}
                  onChange={(e) => handleInputChange('implementationMode', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="direct">Direct Implementation</option>
                  <option value="ngo">Through NGO Partners</option>
                  <option value="hybrid">Hybrid (Direct + Partnerships)</option>
                  <option value="foundation">Company Foundation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Partnership Preference</label>
                <textarea
                  value={formData.partnershipPreference}
                  onChange={(e) => handleInputChange('partnershipPreference', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows="3"
                  placeholder="Describe your preferences for NGO partnerships, selection criteria, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monitoring Mechanism</label>
                <textarea
                  value={formData.monitoringMechanism}
                  onChange={(e) => handleInputChange('monitoringMechanism', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows="3"
                  placeholder="How will you monitor and evaluate CSR activities?"
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Review & Compliance</h3>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-teal-50 rounded-lg">
                <h4 className="text-sm font-semibold text-teal-900 mb-2">Company Details</h4>
                <p className="text-sm text-teal-700">{formData.companyName || 'Not provided'}</p>
                <p className="text-xs text-teal-600">{formData.sector || 'Sector not specified'}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">CSR Budget</h4>
                <p className="text-sm text-blue-700">₹{parseFloat(formData.csrBudget || 0).toLocaleString('en-IN')}</p>
                <p className="text-xs text-blue-600">Annual allocation</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">Focus Areas</h4>
                <p className="text-sm text-purple-700">{formData.focusAreas.length} selected</p>
                <p className="text-xs text-purple-600">Schedule VII compliance</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-semibold text-green-900 mb-2">Committee</h4>
                <p className="text-sm text-green-700">{formData.committeeMembers.length} members</p>
                <p className="text-xs text-green-600">As per Companies Act</p>
              </div>
            </div>

            {/* Compliance Fields */}
            <div className="space-y-4 mt-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Policy Approval Date</label>
                <input
                  type="date"
                  value={formData.policyApprovalDate}
                  onChange={(e) => handleInputChange('policyApprovalDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Frequency</label>
                <select
                  value={formData.reviewFrequency}
                  onChange={(e) => handleInputChange('reviewFrequency', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="annual">Annual</option>
                  <option value="biannual">Bi-annual</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>

            {/* Ready to Generate */}
            <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-200">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-teal-600 mr-3 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-teal-900">Ready to Generate</p>
                  <p className="text-xs text-teal-700 mt-1">
                    Your CSR policy will be generated in compliance with Section 135 of the Companies Act, 2013
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="text-teal-600 hover:text-teal-700 mb-4 flex items-center"
          >
            ← Back
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Shield className="w-8 h-8 mr-3 text-teal-600" />
                CSR Policy Builder
              </h1>
              <p className="text-gray-600 mt-1">Create a comprehensive CSR policy compliant with Companies Act, 2013</p>
            </div>
          </div>
        </div>

        {error && <AlertBanner type="error" message={error} className="mb-6" />}
        {success && <AlertBanner type="success" message={success} className="mb-6" />}

        {!policy ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Steps Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-4 sticky top-8">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Steps</h3>
                <div className="space-y-2">
                  {steps.map((step) => {
                    const Icon = step.icon;
                    return (
                      <button
                        key={step.number}
                        onClick={() => setCurrentStep(step.number)}
                        className={`w-full flex items-center p-3 rounded-lg text-left transition-colors ${
                          currentStep === step.number
                            ? 'bg-teal-50 text-teal-900 border-2 border-teal-500'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                          currentStep === step.number
                            ? 'bg-teal-500 text-white'
                            : 'bg-gray-300 text-gray-600'
                        }`}>
                          {currentStep > step.number ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <Icon className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium">{step.title}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow p-8 mb-6">
                {renderStepContent()}
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center">
                <Button
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  variant="outline"
                  className="text-gray-600"
                >
                  ← Previous
                </Button>

                {currentStep < 6 ? (
                  <Button
                    onClick={() => setCurrentStep(Math.min(6, currentStep + 1))}
                    className="text-white"
                    style={{ background: '#14B8A6' }}
                  >
                    Next →
                  </Button>
                ) : (
                  <Button
                    onClick={handleGeneratePolicy}
                    disabled={loading}
                    className="text-white"
                    style={{ background: '#14B8A6' }}
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Generate Policy
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Policy Preview
          <div className="bg-white rounded-lg shadow p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">CSR Policy Document</h2>
              <div className="flex gap-3">
                <Button
                  onClick={() => handleDownload('pdf')}
                  variant="outline"
                  className="text-teal-600 border-teal-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleDownload('docx')}
                  variant="outline"
                  className="text-blue-600 border-blue-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Word
                </Button>
                <Button
                  onClick={() => setPolicy(null)}
                  variant="outline"
                >
                  Edit
                </Button>
              </div>
            </div>

            <div className="prose prose-teal max-w-none">
              {policy.policyDocument && (
                <div dangerouslySetInnerHTML={{ __html: policy.policyDocument }} />
              )}
              {policy.document && !policy.policyDocument && (
                <div dangerouslySetInnerHTML={{ __html: policy.document }} />
              )}
              {!policy.policyDocument && !policy.document && (
                <p className="text-gray-600">Policy document generated. Use the download buttons above to save.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSRPolicyBuilderPage;
